const DEFAULT_GROUP_HISTORY_LIMIT = 50;
const MAX_HISTORY_KEYS = 1000;
type HistoryEntry = {
  sender: string;
  body: string;
  timestamp?: number;
  messageId?: string;
};

function evictOldHistoryKeys<T>(
  historyMap: Map<string, T[]>,
  maxKeys: number = MAX_HISTORY_KEYS,
): void {
  if (historyMap.size <= maxKeys) {
    return;
  }
  const keysToDelete = historyMap.size - maxKeys;
  const iterator = historyMap.keys();
  for (let i = 0; i < keysToDelete; i++) {
    const key = iterator.next().value;
    if (key !== undefined) {
      historyMap.delete(key);
    }
  }
}

function appendHistoryEntry<T extends HistoryEntry>(params: {
  historyMap: Map<string, T[]>;
  historyKey: string;
  entry: T;
  limit: number;
}): T[] {
  const { historyMap, historyKey, entry } = params;
  if (params.limit <= 0) {
    return [];
  }
  const history = historyMap.get(historyKey) ?? [];
  history.push(entry);
  while (history.length > params.limit) {
    history.shift();
  }
  if (historyMap.has(historyKey)) {
    // Refresh insertion order so eviction keeps recently used histories.
    historyMap.delete(historyKey);
  }
  historyMap.set(historyKey, history);
  // Evict oldest keys if map exceeds max size to prevent unbounded memory growth
  evictOldHistoryKeys(historyMap);
  return history;
}

function recordPendingHistoryEntryIfEnabled<T extends HistoryEntry>(params: {
  historyMap: Map<string, T[]>;
  historyKey: string;
  entry?: T | null;
  limit: number;
}): T[] {
  if (!params.entry || params.limit <= 0) {
    return [];
  }
  return appendHistoryEntry({
    historyMap: params.historyMap,
    historyKey: params.historyKey,
    entry: params.entry,
    limit: params.limit,
  });
}

// 每个 accountId 独立的 group history map，防止多账户冲突
const accountGroupHistories = new Map<string, Map<string, HistoryEntry[]>>();

/**
 * 记录没有被@，被忽略的群消息到对应 account 的 pending history 中
 */
export async function addUnmentionedHistory(apiRuntime: any, accountId: string, chatId: string, tuituiUserName: any, tuituiAccount: string, text: any, timestamp :any) {
  const senderDesc = tuituiUserName ? `${tuituiUserName} (${tuituiAccount})` : tuituiAccount;

  const entry: HistoryEntry = {
    sender: senderDesc,
    body: text,
    timestamp: timestamp ? Number(timestamp) : Date.now(),
 };

  // 为每个 accountId 维护独立的 group history map
  let groupHistories = accountGroupHistories.get(accountId);
  if (!groupHistories) {
    groupHistories = new Map<string, HistoryEntry[]>();
    accountGroupHistories.set(accountId, groupHistories);
  }

  /**
   * 从配置中解析 history limit
   * dashboard->messages->groupChat->historyLimit
   * 实际上没人配置这个值，为避免每条消息到来时产生不必要的资源消耗，暂不读取 runtime config。
   */
  // const cfg = getOpenClawConfig(apiRuntime);
  // const historyLimit = cfg.messages?.groupChat?.historyLimit ?? DEFAULT_GROUP_HISTORY_LIMIT;
  const historyLimit = DEFAULT_GROUP_HISTORY_LIMIT;

  // 这个API内部没啥神秘的，只是帮你维护map变量。openclaw本身不持有这些数据。
  recordPendingHistoryEntryIfEnabled({
    historyMap: groupHistories,
    historyKey: chatId,
    limit: historyLimit,
    entry: entry,
  });
}

/**
 * 获取指定 account 和群组的 pending history，格式化为 ctx.InboundHistory 所需的格式
 */
export function popUnmentionedHistories(accountId: string, chatId: string): {sender: string, body: string, timestamp?: number}[] {
  const groupHistories = accountGroupHistories.get(accountId);
  if (!groupHistories) {
    return [];
  }
  const his = groupHistories.get(chatId);
  if (!his) {
    return [];
  }

  groupHistories.delete(chatId);

  return his.map((entry) => ({
    sender: entry.sender,
    body: entry.body,
    timestamp: entry.timestamp,
  }));
}
